import { useState, useCallback } from "react";
import { request } from "@utils";
import { useToast } from "@/providers";

export type ExportFormat = "csv" | "xlsx";

export interface ExportParams {
  format?: ExportFormat;
  [key: string]: string | undefined | null;
}

/**
 * Reusable hook for triggering file downloads from export endpoints.
 * Works with any backend export endpoint that returns a file buffer.
 *
 * Usage:
 *   const { exportData, isExporting } = useExport("/api/v1/units/export");
 *   exportData({ format: "xlsx", locationId, status });
 */
export function useExport(endpoint: string) {
  const [isExporting, setIsExporting] = useState(false);
  const { showToast } = useToast();

  const exportData = useCallback(
    async (params: ExportParams = {}) => {
      const format = params.format || "xlsx";

      setIsExporting(true);
      showToast({
        severity: "info",
        summary: "Exporting",
        detail: `Preparing ${format.toUpperCase()} file...`,
        life: 2000,
      });

      try {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value != null && value !== "") {
            queryParams.set(key, value);
          }
        });

        const req = request();
        const response = await req.get(
          `${endpoint}?${queryParams.toString()}`,
          { responseType: "blob" },
        );

        // Extract filename from Content-Disposition header or generate one
        const disposition = response.headers["content-disposition"];
        let filename = `export.${format}`;
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }

        // Trigger browser download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        showToast({
          severity: "success",
          summary: "Export Complete",
          detail: `${filename} downloaded successfully`,
          life: 3000,
        });
      } catch (error: any) {
        showToast({
          severity: "error",
          summary: "Export Failed",
          detail:
            error?.response?.status === 401
              ? "Please log in again"
              : "Something went wrong. Please try again.",
          life: 4000,
        });
      } finally {
        setIsExporting(false);
      }
    },
    [endpoint, showToast],
  );

  return { exportData, isExporting };
}
